import React, { useEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { PartnrMark, type PartnrMarkState } from '@/components/partnr/kit/PartnrMark';
import { FolderFloat, type FolderFloatEntry } from '@/components/items/folder/FolderFloat';
import { getFolderColorById } from '@/lib/folderColors';
import MindfullLogo from '@/components/MindfullLogo';
import { MindfullMark } from '@/components/brand/MindfullMark';
import { cn } from '@/lib/utils';
import { CardFace, CARD_SIZE, type CardKind } from './cards';
import { WaitlistForm } from './WaitlistForm';

/**
 * Section two: one pinned scene, three beats, driven by scroll.
 *   01 Save anything: the pile drops onto the canvas.
 *   02 It finds its own place: the pile sorts itself into folders.
 *   03 Ask for it back: Partnr types a vague question and lifts the chair out.
 * The close (waitlist and footer) ends the same section. Reduced motion gets
 * the finished scene and all three beats as a plain list, no pinning.
 */

// The stage is drawn in design units and scaled to fit its box. A wide box
// gets the landscape layout; a phone gets a narrower, taller one.
type FolderId = 'home' | 'lisbon' | 'reading';

const FOLDERS: { id: FolderId; label: string; color: string; papers: FolderFloatEntry[] }[] = [
  {
    id: 'home',
    label: 'Home',
    color: 'sage',
    papers: [
      { key: 'chair', label: 'Birch chair' },
      { key: 'tiles', label: 'Kitchen tiles' },
      { key: 'bed', label: 'Bedroom palette' },
      { key: 'wknd', label: 'Weekend' },
    ],
  },
  {
    id: 'lisbon',
    label: 'Lisbon',
    color: 'periwinkle',
    papers: [
      { key: 'fjord', label: 'Ferry times' },
      { key: 'memo', label: 'Voice memo' },
      { key: 'cafe', label: 'Café list' },
    ],
  },
  {
    id: 'reading',
    label: 'Reading',
    color: 'coral',
    papers: [
      { key: 'slow', label: 'Slow mornings' },
      { key: 'brief', label: 'brief-v3.pdf' },
      { key: 'quote', label: 'Write it down' },
    ],
  },
];

/** Which folder each card is filed into. */
const FILED: Record<CardKind, FolderId> = {
  sticky: 'home',
  chair: 'home',
  todo: 'home',
  palette: 'home',
  voice: 'lisbon',
  fjord: 'lisbon',
  link: 'reading',
  quote: 'reading',
  pdf: 'reading',
};

interface Layout {
  name: 'wide' | 'tall';
  w: number;
  h: number;
  folder: { w: number; h: number; y: number; x: Record<FolderId, number> };
  /** Card centers and rotation in the pile, in drop order. */
  pile: { kind: CardKind; cx: number; cy: number; r: number }[];
  pileScale: number;
  found: { cx: number; cy: number; scale: number };
  composer: { x: number; y: number; w: number };
}

const WIDE: Layout = {
  name: 'wide',
  w: 760,
  h: 600,
  folder: { w: 190, h: 140, y: 446, x: { home: 20, lisbon: 285, reading: 550 } },
  pile: [
    { kind: 'sticky', cx: 118, cy: 112, r: -6 },
    { kind: 'chair', cx: 340, cy: 116, r: 4 },
    { kind: 'link', cx: 590, cy: 104, r: -3 },
    { kind: 'quote', cx: 136, cy: 304, r: 3 },
    { kind: 'voice', cx: 382, cy: 292, r: -2 },
    { kind: 'fjord', cx: 618, cy: 300, r: 5 },
    { kind: 'todo', cx: 150, cy: 482, r: -4 },
    { kind: 'palette', cx: 392, cy: 474, r: 3 },
    { kind: 'pdf', cx: 628, cy: 470, r: -6 },
  ],
  pileScale: 0.84,
  found: { cx: 380, cy: 252, scale: 1.12 },
  composer: { x: 110, y: 26, w: 540 },
};

const TALL: Layout = {
  name: 'tall',
  w: 380,
  h: 560,
  folder: { w: 120, h: 89, y: 456, x: { home: 0, lisbon: 130, reading: 260 } },
  pile: [
    { kind: 'sticky', cx: 64, cy: 74, r: -6 },
    { kind: 'chair', cx: 190, cy: 80, r: 4 },
    { kind: 'link', cx: 314, cy: 72, r: -3 },
    { kind: 'quote', cx: 70, cy: 222, r: 3 },
    { kind: 'voice', cx: 196, cy: 214, r: -2 },
    { kind: 'fjord', cx: 312, cy: 222, r: 5 },
    { kind: 'todo', cx: 70, cy: 366, r: -4 },
    { kind: 'palette', cx: 192, cy: 360, r: 3 },
    { kind: 'pdf', cx: 316, cy: 362, r: -6 },
  ],
  pileScale: 0.56,
  found: { cx: 190, cy: 240, scale: 0.92 },
  composer: { x: 0, y: 16, w: 380 },
};

const mouth = (L: Layout, id: FolderId) => ({ x: L.folder.x[id] + L.folder.w / 2, y: L.folder.y + L.folder.h * 0.22 });

// Scroll timeline, 0..1 across the pinned section
const T = {
  beat2: 0.34,
  beat3: 0.62,
  foldersIn: [0.26, 0.34],
  composerIn: [0.6, 0.66],
  typing: [0.64, 0.76],
  thinking: [0.76, 0.84],
  rise: [0.8, 0.9],
  ring: [0.86, 0.9],
  receipt: [0.88, 0.92],
  dim: [0.8, 0.88],
};

const QUERY = 'the chair with blue legs from spring';

const BEATS = [
  {
    n: '01',
    title: 'Save anything.',
    body: "Links, screenshots, PDFs, voice notes, the quote you'll want next month. Drop it in from your phone or your browser. Nothing to file.",
  },
  {
    n: '02',
    title: 'It finds its own place.',
    body: 'Mindfull reads what you save and gathers it into boards. Open one and you get a canvas with all the room you need.',
  },
  {
    n: '03',
    title: 'Ask for it back.',
    body: 'Describe it the way you remember it. Partnr finds it, even if you never gave it a name.',
  },
];

/* ── Stage ─────────────────────────────────────────────────────────────── */

function StageCard({ p, index, cfg, L }: { p: MotionValue<number>; index: number; cfg: Layout['pile'][number]; L: Layout }) {
  const { w, h } = CARD_SIZE[cfg.kind];
  const m = mouth(L, FILED[cfg.kind]);
  const drop = L.h * 0.45;
  const enter = 0.02 + index * 0.014;
  const landed = enter + 0.1;
  const sort = 0.38 + index * 0.014;
  const filed = sort + 0.12;
  const chair = cfg.kind === 'chair';
  const F = L.found;
  const S = L.pileScale;

  // Drops from above its spot, lands in the pile, slides into its folder.
  // The chair comes back out in beat three.
  const t = chair ? [0, enter, landed, sort, filed, T.rise[0], T.rise[1], 1] : [0, enter, landed, sort, filed, 1];
  const cx = chair ? [cfg.cx, cfg.cx, cfg.cx, cfg.cx, m.x, m.x, F.cx, F.cx] : [cfg.cx, cfg.cx, cfg.cx, cfg.cx, m.x, m.x];
  const cy = chair
    ? [cfg.cy - drop, cfg.cy - drop, cfg.cy, cfg.cy, m.y, m.y, F.cy, F.cy]
    : [cfg.cy - drop, cfg.cy - drop, cfg.cy, cfg.cy, m.y, m.y];
  const filedScale = (L.folder.w / 190) * 0.22;
  const sc = chair ? [S * 1.1, S * 1.1, S, S, filedScale, filedScale, F.scale, F.scale] : [S * 1.1, S * 1.1, S, S, filedScale, filedScale];
  const rot = chair ? [cfg.r * 3, cfg.r * 3, cfg.r, cfg.r, 0, 0, 0, 0] : [cfg.r * 3, cfg.r * 3, cfg.r, cfg.r, 0, 0];

  const x = useTransform(p, t, cx.map((v) => v - w / 2));
  const y = useTransform(p, t, cy.map((v) => v - h / 2));
  const scale = useTransform(p, t, sc);
  const rotate = useTransform(p, t, rot);
  const opacity = useTransform(
    p,
    chair ? [0, enter, enter + 0.04, filed - 0.04, filed, T.rise[0], T.rise[0] + 0.03, 1] : [0, enter, enter + 0.04, filed - 0.04, filed, 1],
    chair ? [0, 0, 1, 1, 0, 0, 1, 1] : [0, 0, 1, 1, 0, 0],
  );
  const ring = useTransform(p, T.ring, [0, 1]);

  return (
    <motion.div className="absolute left-0 top-0" style={{ x, y, scale, rotate, opacity, zIndex: chair ? 30 : 20 }}>
      <CardFace kind={cfg.kind} />
      {chair && (
        <motion.div
          aria-hidden
          style={{ opacity: ring }}
          className="pointer-events-none absolute -inset-[5px] rounded-[18px] border-2 border-signal"
        />
      )}
    </motion.div>
  );
}

function StageFolder({ p, folder, L }: { p: MotionValue<number>; folder: (typeof FOLDERS)[number]; L: Layout }) {
  const palette = getFolderColorById(folder.color);
  const appear = useTransform(p, T.foldersIn, [0, 1]);
  const dim = useTransform(p, T.dim, [1, folder.id === 'home' ? 1 : 0.35]);
  const opacity = useTransform([appear, dim], ([a, d]: number[]) => a * d);
  const y = useTransform(p, T.foldersIn, [36, 0]);

  return (
    <motion.div className="absolute" style={{ left: L.folder.x[folder.id], top: L.folder.y, opacity, y, zIndex: 10 }}>
      <FolderFloat
        items={folder.papers}
        label={folder.label}
        sublabel={`${folder.papers.length * 3 + 2} items`}
        folderColor={palette.folderDark}
        frontColor={palette.folder}
        paperColor={palette.label}
        itemColor="hsl(var(--paper-raised))"
        itemTextColor="hsl(var(--ink))"
        labelColor={palette.labelText}
        width={L.folder.w}
        height={L.folder.h}
        radius={14 * (L.folder.w / 190)}
        spread={170 * (L.folder.w / 190)}
        lift={24 * (L.folder.w / 190)}
        openDelay={80}
      />
    </motion.div>
  );
}

function Composer({ p, L }: { p: MotionValue<number>; L: Layout }) {
  const opacity = useTransform(p, T.composerIn, [0, 1]);
  const y = useTransform(p, T.composerIn, [14, 0]);
  const typed = useTransform(p, T.typing, [0, QUERY.length]);
  const [count, setCount] = useState(() => Math.round(typed.get()));
  const [mark, setMark] = useState<PartnrMarkState>('idle');

  useMotionValueEvent(typed, 'change', (v) => setCount(Math.round(v)));
  useMotionValueEvent(p, 'change', (v) => {
    setMark(v >= T.thinking[0] && v < T.thinking[1] ? 'thinking' : 'idle');
  });

  const text = QUERY.slice(0, count);
  const done = count >= QUERY.length;
  return (
    <motion.div
      style={{ opacity, y, left: L.composer.x, top: L.composer.y, width: L.composer.w, zIndex: 40 }}
      className="absolute flex h-14 items-center gap-3 rounded-full border border-line bg-paper-raised pl-5 pr-2 shadow-float"
    >
      <PartnrMark size={18} state={mark} className="text-ink" />
      <span className={cn('min-w-0 flex-1 truncate text-ink', L.name === 'tall' ? 'text-[14px]' : 'text-[16px]')}>
        {text || <span className="text-ink-3">What are we working on?</span>}
        {!done && text && <span className="ml-px inline-block h-[18px] w-[1.5px] translate-y-[3px] animate-pulse bg-ink" />}
      </span>
      <span
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-200',
          text ? 'bg-ink text-paper-raised' : 'bg-wash text-ink-3',
        )}
      >
        <ArrowUp className="h-4 w-4" />
      </span>
    </motion.div>
  );
}

function Receipt({ p, L }: { p: MotionValue<number>; L: Layout }) {
  const opacity = useTransform(p, T.receipt, [0, 1]);
  const y = useTransform(p, T.receipt, [8, 0]);
  return (
    <motion.div
      style={{ opacity, y, top: L.found.cy + (208 * L.found.scale) / 2 + 16, zIndex: 40 }}
      className="absolute inset-x-0 flex justify-center"
    >
      <span className="inline-flex items-center gap-2 rounded-full border border-line bg-paper-raised px-3.5 py-1.5 text-[13px] text-ink-2 shadow-sm">
        <PartnrMark size={14} className="text-ink-3" />
        Found it in <span className="font-medium text-ink">Home</span>
        <span className="font-mono text-[11px] text-ink-3">Saved 14 Apr</span>
      </span>
    </motion.div>
  );
}

/** Picks a layout for the box and scales it to fit. Reacts to the box only;
 *  the stage is absolutely positioned, so scaling it never resizes the box. */
function useFit(ref: React.RefObject<HTMLDivElement>) {
  const [fit, setFit] = useState<{ L: Layout; scale: number }>({ L: WIDE, scale: 1 });
  const last = useRef({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (Math.abs(width - last.current.w) <= 1 && Math.abs(height - last.current.h) <= 1) return;
      last.current = { w: width, h: height };
      // Phones and narrow columns; a desktop column is near-square and stays wide
      const L = width < 600 || width / Math.max(height, 1) < 0.75 ? TALL : WIDE;
      setFit({ L, scale: Math.min(width / L.w, height / L.h, 1.1) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return fit;
}

function Stage({ p, className }: { p: MotionValue<number>; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const { L, scale } = useFit(box);
  return (
    <div ref={box} className={cn('relative min-h-0 min-w-0', className)}>
      {/* Keyed by layout: the scroll transforms are built from its numbers */}
      <div
        key={L.name}
        className="absolute left-1/2 top-1/2"
        style={{ width: L.w, height: L.h, transform: `translate(-50%, -50%) scale(${scale})` }}
      >
        {FOLDERS.map((f) => (
          <StageFolder key={f.id} p={p} folder={f} L={L} />
        ))}
        {L.pile.map((cfg, i) => (
          <StageCard key={cfg.kind} p={p} index={i} cfg={cfg} L={L} />
        ))}
        <Composer p={p} L={L} />
        <Receipt p={p} L={L} />
      </div>
    </div>
  );
}

/* ── Beats ─────────────────────────────────────────────────────────────── */

function Beat({ p, i }: { p: MotionValue<number>; i: number }) {
  const a = [0, T.beat2, T.beat3][i];
  const b = [T.beat2, T.beat3, 1][i];
  const opacity = useTransform(
    p,
    i === 0 ? [0, b - 0.04, b] : i === 2 ? [a - 0.02, a + 0.02, 1] : [a - 0.02, a + 0.02, b - 0.04, b],
    i === 0 ? [1, 1, 0] : i === 2 ? [0, 1, 1] : [0, 1, 1, 0],
  );
  const y = useTransform(p, i === 0 ? [0, b] : [a - 0.02, a + 0.02], i === 0 ? [0, -10] : [14, 0]);
  const beat = BEATS[i];
  return (
    <motion.div style={{ opacity, y }} className="absolute inset-x-0 top-0">
      <BeatCopy beat={beat} />
    </motion.div>
  );
}

function BeatCopy({ beat }: { beat: (typeof BEATS)[number] }) {
  return (
    <>
      <p className="mb-4 font-mono text-[12px] tracking-[0.08em] text-ink-3">{beat.n}</p>
      <h3 className="font-display text-[clamp(1.9rem,3.6vw,3.4rem)] font-semibold leading-[1.02] tracking-[-0.03em] text-ink">
        {beat.title}
      </h3>
      <p className="mt-4 max-w-[26rem] text-[16px] leading-[26px] text-ink-2 lg:text-[17px] lg:leading-[28px]">{beat.body}</p>
    </>
  );
}

function Progress({ p }: { p: MotionValue<number> }) {
  return (
    <div className="flex gap-1.5" aria-hidden>
      {[0, 1, 2].map((i) => (
        <ProgressBar key={i} p={p} a={[0, T.beat2, T.beat3][i]} b={[T.beat2, T.beat3, 0.92][i]} />
      ))}
    </div>
  );
}

function ProgressBar({ p, a, b }: { p: MotionValue<number>; a: number; b: number }) {
  const scaleX = useTransform(p, [a, b], [0, 1]);
  return (
    <span className="relative h-[2px] w-10 overflow-hidden rounded-full bg-line">
      <motion.span style={{ scaleX }} className="absolute inset-0 origin-left bg-ink" />
    </span>
  );
}

/* ── Close ─────────────────────────────────────────────────────────────── */

function Close() {
  return (
    <div className="relative overflow-hidden border-t border-line bg-paper">
      <div className="mx-auto flex max-w-6xl flex-col items-center px-5 pb-16 pt-28 text-center sm:px-8 sm:pt-36">
        <MindfullMark size={44} motion="loop" className="mb-8 text-ink" />
        <h2 className="max-w-[14ch] font-display text-[clamp(2.4rem,6vw,5.6rem)] font-semibold leading-[1] tracking-[-0.035em] text-ink">
          Let your mind be full somewhere else.
        </h2>
        <p className="mt-6 max-w-[30rem] text-[17px] leading-[27px] text-ink-2">
          Mindfull is in private beta. Join the waitlist and we'll send an invite when there's room.
        </p>
        <WaitlistForm id="close-email" className="mt-9 w-full max-w-[27rem]" />
      </div>

      <footer className="relative mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 pb-8 pt-10 sm:flex-row sm:px-8">
        <MindfullLogo size="sm" />
        <nav className="flex gap-6 text-[13px] text-ink-3">
          <a href="#" className="transition-colors duration-150 hover:text-ink">Privacy</a>
          <a href="#" className="transition-colors duration-150 hover:text-ink">Terms</a>
          <a href="#" className="transition-colors duration-150 hover:text-ink">Contact</a>
        </nav>
        <p className="font-mono text-[11px] text-ink-3">© 2026 Mindfull</p>
      </footer>

      {/* The wordmark, set huge and cropped by the page edge */}
      <p
        aria-hidden
        className="pointer-events-none -mb-[0.24em] select-none text-center font-display text-[24vw] font-semibold leading-[0.8] tracking-[-0.05em] text-wash"
      >
        Mindfull
      </p>
    </div>
  );
}

/* ── Section ───────────────────────────────────────────────────────────── */

export function Story() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion() ?? false;
  const { scrollYProgress } = useScroll(reduced ? {} : { target: ref, offset: ['start start', 'end end'] });
  const finished = useMotionValue(1);

  if (reduced) {
    return (
      <section aria-labelledby="story-title" className="bg-paper">
        <h2 id="story-title" className="sr-only">How Mindfull works</h2>
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
          <div className="space-y-12">
            {BEATS.map((b) => (
              <div key={b.n}>
                <BeatCopy beat={b} />
              </div>
            ))}
          </div>
          <Stage p={finished} className="h-[420px] sm:h-[560px]" />
        </div>
        <Close />
      </section>
    );
  }

  return (
    <section aria-labelledby="story-title" className="bg-paper">
      <h2 id="story-title" className="sr-only">How Mindfull works</h2>
      <div ref={ref} className="relative h-[380vh]">
        <div className="sticky top-0 flex h-[100svh] flex-col gap-4 overflow-hidden px-5 pb-6 pt-20 sm:px-8 lg:grid lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-10 lg:px-12 lg:py-0">
          <div className="relative flex shrink-0 flex-col lg:justify-center">
            <div className="mb-6 lg:mb-10">
              <Progress p={scrollYProgress} />
            </div>
            {/* Beats share one slot; the tallest sets its height */}
            <div className="relative h-[200px] sm:h-[190px] lg:h-[300px]">
              {BEATS.map((_, i) => (
                <Beat key={i} p={scrollYProgress} i={i} />
              ))}
            </div>
          </div>
          <Stage p={scrollYProgress} className="flex-1 lg:h-full" />
        </div>
      </div>
      <Close />
    </section>
  );
}
